import React, { useMemo } from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { DailySummary } from '../../hooks/useAnalytics'

interface TrendChartProps {
  data: DailySummary[]
  metrics: {
    key: keyof DailySummary
    label: string
    color: string
  }[]
  title?: string
  height?: number
}

export default function TrendChart({
  data,
  metrics,
  title,
  height = 300,
}: TrendChartProps) {
  const theme = useTheme()
  
  const chartData = useMemo(() => {
    return data.map((day) => ({
      date: new Date(day.summary_date).toLocaleDateString('da-DK', {
        day: 'numeric',
        month: 'short',
      }),
      ...metrics.reduce((acc, metric) => ({
        ...acc,
        [metric.key]: day[metric.key] || 0,
      }), {}),
    }))
  }, [data, metrics])

  if (data.length === 0) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.paper',
          borderRadius: 2,
          border: '1px dashed',
          borderColor: 'divider',
        }}
      >
        <Typography color="text.secondary">
          Ingen data tilgængelig for den valgte periode
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      {title && (
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          {title}
        </Typography>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <LineChart
          data={chartData}
          margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={theme.palette.divider}
            vertical={false}
          />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
            tickLine={false}
            axisLine={{ stroke: theme.palette.divider }}
          />
          <YAxis
            tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
            tickLine={false}
            axisLine={false}
            width={40}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 8,
              boxShadow: theme.shadows[2],
            }}
            labelStyle={{
              color: theme.palette.text.primary,
              fontWeight: 600,
              marginBottom: 4,
            }}
          />
          {metrics.length > 1 && (
            <Legend
              wrapperStyle={{
                paddingTop: 16,
              }}
            />
          )}
          {metrics.map((metric) => (
            <Line
              key={metric.key}
              type="monotone"
              dataKey={metric.key}
              name={metric.label}
              stroke={metric.color}
              strokeWidth={2}
              dot={false}
              activeDot={{
                r: 6,
                fill: metric.color,
                stroke: theme.palette.background.paper,
                strokeWidth: 2,
              }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Box>
  )
}
